import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";
import { comics } from "@/mocks/comics";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id)
    .slice(0, 4);

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 10) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话${i === 0 ? " - 最新" : ""}`,
    date: new Date(2026, 6, 3 - i).toISOString().split("T")[0],
  }));

  const breadcrumbs = [
    { label: "首页", to: "/" },
    { label: comic.category, to: `/category/${getCategorySlug(comic.category)}` },
    { label: comic.title, to: `/article/${comic.id}` },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.label,
          item: `http://jiarenmei.com/${item.to}`,
        })),
      },
      {
        "@type": "Article",
        headline: `${comic.title} - 韩漫画网`,
        description: comic.description,
        author: { "@type": "Person", name: comic.author },
        datePublished: "2025-06-01",
        dateModified: comic.updateTime,
        url: `http://jiarenmei.com/article/${comic.id}`,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: comic.rating,
          bestRating: "10",
          ratingCount: Math.floor(Math.random() * 50000) + 10000,
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${comic.title} - 韩漫画网高清免费韩国漫画在线阅读`}
        description={comic.description}
        canonicalUrl={`http://jiarenmei.com/article/${comic.id}`}
        jsonLd={jsonLd}
        type="article"
        imageUrl={comic.coverImage}
      />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-50 py-8 md:py-12">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              {breadcrumbs.map((item, index) => (
                <span key={item.to} className="flex items-center gap-2">
                  {index > 0 && (
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </div>
                  )}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-900 font-medium">{item.label}</span>
                  ) : (
                    <Link to={item.to} className="hover:text-primary-500 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>

            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0 w-full md:w-[320px]">
                <div className="rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
                <div className="flex gap-3 mt-4">
                  <Link
                    to="#"
                    className="flex-1 px-5 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm text-center whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i>
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-900">
                    {comic.title}
                  </h1>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                    comic.status === "连载中"
                      ? "bg-accent-100 text-accent-700"
                      : "bg-background-200 text-foreground-600"
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 flex-wrap mb-4">
                  <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span className="text-sm text-foreground-500">分类：<Link to={`/category/${getCategorySlug(comic.category)}`} className="text-primary-500 hover:text-primary-600 font-medium">{comic.category}</Link></span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
                </div>

                <div className="flex items-center gap-2 mb-5">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <div key={star} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${star <= Math.floor(comic.rating / 2) ? "ri-star-fill text-primary-500" : "ri-star-line text-foreground-300"}`}></i>
                      </div>
                    ))}
                  </div>
                  <span className="text-lg font-extrabold text-primary-500">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">{comic.views} 阅读</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">章节列表</h3>
                  <div className="space-y-1.5 max-h-[360px] overflow-y-auto">
                    {mockChapters.map((ch) => (
                      <Link
                        key={ch.num}
                        to="#"
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors">
                            {ch.title}
                          </span>
                          {ch.num === comic.chapters && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-primary-600">
                              NEW
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </Link>
                    ))}
                  </div>
                  {comic.chapters > 10 && (
                    <button className="mt-3 w-full py-2.5 rounded-lg bg-background-100 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                      查看全部 {comic.chapters} 话
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-900 mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {relatedComics.map((c) => (
                <Link
                  key={c.id}
                  to={`/article/${c.id}`}
                  className="group cursor-pointer"
                  data-product-shop=""
                >
                  <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3 bg-background-200">
                    <img
                      src={c.coverImage}
                      alt={c.title}
                      className="w-full h-full object-cover object-top transition-transform duration-400 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-primary-500 text-background-50">
                        {c.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 pt-12">
                      <h3 className="text-sm font-semibold text-background-50 group-hover:text-primary-300 transition-colors">
                        {c.title}
                      </h3>
                      <p className="text-xs text-background-50/70 mt-1">{c.author}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function getCategorySlug(category: string): string {
  const map: Record<string, string> = {
    恋爱: "romance",
    奇幻: "fantasy",
    动作: "action",
    悬疑: "thriller",
    日常: "slice-of-life",
    搞笑: "comedy",
    古风: "historical",
    恐怖: "horror",
  };
  return map[category] || "all";
}